import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ServicesOverview() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchData = () => {
    if (!companyId) return;
    setLoading(true);
    let url = `${API}/companies/${companyId}/services/overview`;
    const params = [];
    if (dateFrom) params.push(`date_from=${dateFrom}`);
    if (dateTo) params.push(`date_to=${dateTo}`);
    if (params.length) url += '?' + params.join('&');

    fetch(url, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : {})
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [companyId]);

  if (initialLoading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!data) return <div style={{ padding: 40 }}>No data available</div>;

  const fmt = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

  const cardStyle = { background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', flex: '1 1 200px', minWidth: 180 };
  const sectionStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
  const badgeStyle = (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: color + '20', color });

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>🛠️ Services Overview</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }} />
          <span style={{ color: '#94a3b8' }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }} />
          <button onClick={fetchData} style={{ padding: '6px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}>Filter</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ ...cardStyle, borderLeft: '4px solid #3b82f6' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Total Services</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#3b82f6' }}>{data.total_services || 0}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #22c55e' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Active Services</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#22c55e' }}>{data.active_services || 0}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #8b5cf6' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Active Engagements</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#8b5cf6' }}>{data.active_engagements || 0}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #f59e0b' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Total Clients</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f59e0b' }}>{data.total_clients || 0}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #22c55e' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Expected Revenue</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#22c55e' }}>{fmt(data.expected_revenue)}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #ef4444' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Operating Margin</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: data.operational_margin >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(data.operational_margin)}</div>
        </div>
      </div>

      {/* Service Summary Table */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📋 Service Summary</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Service</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Active Eng.</th>
                <th style={thStyle}>Clients</th>
                <th style={thStyle}>Revenue</th>
                <th style={thStyle}>Cost</th>
                <th style={thStyle}>Margin</th>
                <th style={thStyle}>Owner</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data.services) && data.services.map(s => (
                <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/company/services/catalog?view=${s.id}`)}>
                  <td style={tdStyle}><span style={{ fontWeight: 600 }}>{s.name}</span><br /><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{s.service_code}</span></td>
                  <td style={tdStyle}>{s.category || '—'}</td>
                  <td style={tdStyle}><span style={badgeStyle('#6366f1')}>{(s.service_type || '').replace('_', ' ')}</span></td>
                  <td style={tdStyle}>{s.active_engagements}</td>
                  <td style={tdStyle}>{s.clients}</td>
                  <td style={tdStyle}>{fmt(s.collected_revenue)}</td>
                  <td style={tdStyle}>{fmt(s.actual_cost)}</td>
                  <td style={{ ...tdStyle, color: s.operational_margin >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>{fmt(s.operational_margin)}</td>
                  <td style={tdStyle}>{s.owner || '—'}</td>
                </tr>
              ))}
              {(!data.services || data.services.length === 0) && <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No active services</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Category Performance & Pending Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📊 Category Performance</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Services</th>
                <th style={thStyle}>Engagements</th>
                <th style={thStyle}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data.categories) && data.categories.map(c => (
                <tr key={c.id}>
                  <td style={tdStyle}>{c.name}</td>
                  <td style={tdStyle}>{c.services_count}</td>
                  <td style={tdStyle}>{c.engagements}</td>
                  <td style={tdStyle}>{fmt(c.collected_revenue)}</td>
                </tr>
              ))}
              {(!data.categories || data.categories.length === 0) && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No categories</td></tr>}
            </tbody>
          </table>
        </div>

        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>⚠️ Pending Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, background: '#fef3c7', borderRadius: 8 }}>
              <span style={{ fontSize: '0.85rem' }}>Services missing owner</span>
              <span style={{ fontWeight: 700, color: '#f59e0b' }}>{data.pending_actions?.missing_owner || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, background: '#fee2e2', borderRadius: 8 }}>
              <span style={{ fontSize: '0.85rem' }}>Engagements missing ops link</span>
              <span style={{ fontWeight: 700, color: '#ef4444' }}>{data.pending_actions?.missing_ops_link || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Engagements & Ending Soon */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>🕐 Recent Engagements</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Service</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data.recent_engagements) && data.recent_engagements.map(e => (
                <tr key={e.id}>
                  <td style={tdStyle}>{e.engagement_code}</td>
                  <td style={tdStyle}>{e.client_name}</td>
                  <td style={tdStyle}>{e.service || '—'}</td>
                  <td style={tdStyle}><span style={badgeStyle(e.status === 'active' ? '#22c55e' : e.status === 'completed' ? '#3b82f6' : '#f59e0b')}>{e.status}</span></td>
                  <td style={tdStyle}>{fmt(e.expected_revenue)}</td>
                </tr>
              ))}
              {(!data.recent_engagements || data.recent_engagements.length === 0) && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No recent engagements</td></tr>}
            </tbody>
          </table>
        </div>

        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>⏰ Ending Soon / Renewals Due</h3>
          {Array.isArray(data.ending_soon) && data.ending_soon.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Code</th>
                  <th style={thStyle}>Client</th>
                  <th style={thStyle}>End Date</th>
                </tr>
              </thead>
              <tbody>
                {data.ending_soon.map(e => (
                  <tr key={e.id}>
                    <td style={tdStyle}>{e.engagement_code}</td>
                    <td style={tdStyle}>{e.client_name}</td>
                    <td style={{ ...tdStyle, color: '#ef4444', fontWeight: 600 }}>{e.expected_end_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No engagements ending soon</p>}

          {Array.isArray(data.renewals_due) && data.renewals_due.length > 0 && (
            <>
              <h4 style={{ margin: '12px 0 8px', fontSize: '0.9rem', fontWeight: 600, color: '#f59e0b' }}>🔄 Renewals Due</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Code</th>
                    <th style={thStyle}>Client</th>
                    <th style={thStyle}>Renewal Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.renewals_due.map(e => (
                    <tr key={e.id}>
                      <td style={tdStyle}>{e.engagement_code}</td>
                      <td style={tdStyle}>{e.client_name}</td>
                      <td style={{ ...tdStyle, color: '#f59e0b', fontWeight: 600 }}>{e.renewal_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
